import { masterApi } from "api/masterApi";

const DUPLICATE_TEXT = /duplicate|already\s+exist|another\s+item|تکراری/i;
const text = (value) => (typeof value === "string" ? value : "");

const getPayload = (response) =>
  response?.message?.validate ??
  response?.message?.items ??
  response?.message?.results ??
  response?.message?.duplicates ??
  response?.message ??
  response?.data?.validate ??
  response?.data?.items ??
  response?.data?.results ??
  response?.data?.duplicates ??
  response?.data;

const matchesItem = (result, item) => {
  if (!result || typeof result !== "object") return false;

  const resultId = result.libraryId ?? result.library_id ?? result.id;
  if (resultId && item.libraryId) return resultId === item.libraryId;

  return (
    String(result.title ?? "").toLowerCase() ===
      String(item.title ?? "").toLowerCase() &&
    String(result.parent ?? "") === String(item.parent ?? "") &&
    Boolean(result.is_folder) === Boolean(item.is_folder)
  );
};

const messageOf = (result) =>
  text(result) ||
  text(result?.message) ||
  text(result?.reason) ||
  text(result?.error?.message) ||
  text(result?.error);

const resultIsDuplicate = (result) =>
  Boolean(
    result?.duplicate === true ||
      result?.isDuplicate === true ||
      result?.is_duplicate === true ||
      result?.exists === true ||
      result?.is_exist === true ||
      DUPLICATE_TEXT.test(messageOf(result))
  );

const resultIsInvalid = (result) =>
  result === false ||
  result?.valid === false ||
  result?.isValid === false ||
  result?.is_valid === false ||
  Boolean(result?.error) ||
  Number(result?.status) >= 400 ||
  result?.status === "failed";

// Preserve each item's own verdict and message, including non-duplicate errors.
export const parseLibraryValidation = (response, items) => {
  const payload = getPayload(response);
  const requestError = Boolean(
    response?.error ||
      Number(response?.status) >= 400 ||
      response?.status === "failed" ||
      response?.status === "axios_failed"
  );
  const results = items.map((item, index) => {
    const result = Array.isArray(payload)
      ? payload.find((entry) => matchesItem(entry, item)) ??
        (payload.length === items.length ? payload[index] : undefined)
      : payload;
    const message =
      messageOf(result) ||
      messageOf(response?.message) ||
      messageOf(response?.error);
    if (
      resultIsDuplicate(result) ||
      (!Array.isArray(payload) && resultIsDuplicate(response))
    )
      return {
        status: "duplicate",
        message: message || "Duplicate library item",
      };
    if (
      resultIsInvalid(result) ||
      (requestError && result === undefined) ||
      (!Array.isArray(payload) && requestError)
    )
      return {
        status: "failed",
        message: message || "Could not validate this file. Please try again.",
      };
    return { status: "valid", message: "" };
  });
  const indexesOf = (status) =>
    results.reduce(
      (indexes, result, index) =>
        result.status === status ? [...indexes, index] : indexes,
      []
    );
  const failedIndexes = indexesOf("failed");
  return {
    duplicateIndexes: indexesOf("duplicate"),
    failedIndexes,
    requestFailed: items.length > 0 && failedIndexes.length === items.length,
    response,
    results,
  };
};

export const validateLibraryItems = async (items) => {
  const response = await masterApi("post", "/library/validate", {
    validate: items,
  });

  return parseLibraryValidation(response, items);
};

/**
 * If a batch is rejected without per-item results, validate files separately
 * so a single business error is not attributed to every file.
 */
export const validateLibraryBatch = async (items) => {
  const batch = await validateLibraryItems(items);
  const wholeConflict =
    !Array.isArray(getPayload(batch.response)) &&
    (batch.response?.status === 409 ||
      batch.response?.status === 422 ||
      batch.response?.status === 400);
  if (!wholeConflict || items.length < 2) return batch;

  const singles = await Promise.all(
    items.map((item) => validateLibraryItems([item]))
  );
  const results = singles.map((single) => single.results[0]);
  const indexesOf = (status) =>
    results.reduce(
      (indexes, result, index) =>
        result.status === status ? [...indexes, index] : indexes,
      []
    );
  const failedIndexes = indexesOf("failed");

  return {
    duplicateIndexes: indexesOf("duplicate"),
    failedIndexes,
    requestFailed: failedIndexes.length === items.length,
    response: batch.response,
    results,
  };
};

export const toLibraryValidationItem = ({
  id,
  libraryId,
  title,
  parent,
  is_folder,
  size,
}) => ({
  ...(libraryId || id ? { libraryId: libraryId || id } : {}),
  title,
  parent: parent?.id ?? parent ?? null,
  is_folder: Boolean(is_folder),
  ...(!is_folder && Number.isFinite(size) && size >= 0 ? { size } : {}),
});
